import type {
    ComponentSuggestionPayload,
    ComponentSuggestionResponse,
} from '@/types/apps/component-suggestion.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

type ComponentSuggestionListResponse = ApiResponse<
    PaginatedResult<ComponentSuggestionResponse>
>;

export class ComponentSuggestionService
    implements Showable, Updatable, Deletable, Creatable, Indexable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    protected endpoints = {
        base: () => '/api/part-suggestion',
        detail: (id: string) => `/api/part-suggestion/${id}`,
        approveBulk: () => '/api/part-suggestion/part-suggestions',
    };

    public index(
        params?: QueryParams
    ): Promise<ComponentSuggestionListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<ComponentSuggestionResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: ComponentSuggestionPayload
    ): Promise<ApiResponse<ComponentSuggestionResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public updateBulk(payload: {
        partSuggestions: ComponentSuggestionPayload[];
    }): Promise<ApiResponse<ComponentSuggestionResponse>> {
        return this.apiClient.putApi(this.endpoints.approveBulk(), payload);
    }

    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }
}
